import { forgetRegionChoice } from "@/services/regionCookie";

const AUTH_TOKEN_STORAGE_KEY = "token";
const REMEMBER_KEY = "auth-remember";
const AUTH_TOKEN_CHANGE_EVENT = "copilot-auth-token-changed";

const TOKEN_KEYS = ["access_token", "accessToken", "token"] as const;
const NESTED_TOKEN_KEYS = ["data", "result", "meta", "auth"] as const;

function getSessionCookie() {
  if (typeof window === "undefined") return true;
  return document.cookie.indexOf('auth_session=1') !== -1;
}

function setSessionCookie() {
  if (typeof window === "undefined") return;
  document.cookie = "auth_session=1; path=/; samesite=lax";
}

export function getStoredAuthToken(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  const localToken = window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)?.trim();
  if (localToken) {
    const remember = window.localStorage.getItem(REMEMBER_KEY) === "true";
    if (!remember && !getSessionCookie()) {
      clearStoredAuthToken();
      return null;
    }
    return localToken;
  }

  return null;
}

export function storeAuthToken(token: string, remember = true): void {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
  window.localStorage.setItem(REMEMBER_KEY, remember ? "true" : "false");
  
  if (!remember) {
    setSessionCookie();
  }
  
  // Clean up legacy sessionStorage if it exists
  window.sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  
  notifyAuthTokenChanged();
}

export function clearStoredAuthToken(): void {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  window.localStorage.removeItem(REMEMBER_KEY);
  document.cookie = "auth_session=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT";
  // The region pin describes the account that is going away — "global" most of
  // all, which would otherwise keep this browser on the Global site as an
  // anonymous visitor instead of being geolocated like any other guest.
  forgetRegionChoice();
  window.sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  
  notifyAuthTokenChanged();
}

export function subscribeToAuthTokenChange(listener: () => void): () => void {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const handleChange = () => listener();

  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === AUTH_TOKEN_STORAGE_KEY) {
      handleChange();
    }
  };

  window.addEventListener(AUTH_TOKEN_CHANGE_EVENT, handleChange);
  window.addEventListener("storage", handleStorageChange);

  return () => {
    window.removeEventListener(AUTH_TOKEN_CHANGE_EVENT, handleChange);
    window.removeEventListener("storage", handleStorageChange);
  };
}

// Notify and subscribe helpers for user updates (e.g., profile changes)
const AUTH_USER_CHANGE_EVENT = "copilot-user-updated";

export function notifyUserChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(AUTH_USER_CHANGE_EVENT));
}

export function subscribeToUserChange(listener: () => void): () => void {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const handle = () => listener();
  window.addEventListener(AUTH_USER_CHANGE_EVENT, handle);

  return () => window.removeEventListener(AUTH_USER_CHANGE_EVENT, handle);
}

// Cross-tab "the active profile/identity deliberately changed" signal.
//
// The token `storage` event (subscribeToAuthTokenChange) is NOT enough on its
// own here: it also fires on routine background token refresh, and it can't
// tell "same user, rotated token" apart from "switched to a different user".
// A profile switch needs other tabs to fully reset their user-specific state,
// which we don't want to do on every refresh. This channel is posted ONLY at
// a real switch, so listeners can react with a hard reset + atomic loading
// transition without false-triggering.
//
// A single module-level BroadcastChannel is used for both posting and
// subscribing: per spec a channel never receives its own postMessage, so the
// switching tab (which posts) doesn't re-handle its own event, while every
// other same-origin tab does. Guarded for SSR and for browsers without
// BroadcastChannel (the token `storage` event remains the fallback path).
const PROFILE_SWITCH_CHANNEL_NAME = "ijaazah-auth-sync";
const PROFILE_SWITCH_MESSAGE = "profile-switched";

let profileSwitchChannel: BroadcastChannel | null = null;

function getProfileSwitchChannel(): BroadcastChannel | null {
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") {
    return null;
  }
  if (!profileSwitchChannel) {
    profileSwitchChannel = new BroadcastChannel(PROFILE_SWITCH_CHANNEL_NAME);
  }
  return profileSwitchChannel;
}

export function notifyProfileSwitched(): void {
  getProfileSwitchChannel()?.postMessage({ type: PROFILE_SWITCH_MESSAGE, at: Date.now() });
}

export function subscribeToProfileSwitch(listener: () => void): () => void {
  const channel = getProfileSwitchChannel();
  if (!channel) {
    return () => undefined;
  }

  const handle = (event: MessageEvent) => {
    if ((event.data as { type?: string } | null)?.type === PROFILE_SWITCH_MESSAGE) {
      listener();
    }
  };

  channel.addEventListener("message", handle);
  return () => channel.removeEventListener("message", handle);
}

export function resolveAuthToken(
  payload: unknown,
  headers?: Record<string, unknown> | null
): string | null {
  const headerToken = extractHeaderToken(headers);
  if (headerToken) {
    return headerToken;
  }

  return extractTokenFromValue(payload);
}

function extractHeaderToken(headers?: Record<string, unknown> | null): string | null {
  if (!headers) {
    return null;
  }

  const authorizationHeader = readString(headers.authorization) ?? readString(headers.Authorization);
  if (!authorizationHeader) {
    return null;
  }

  return authorizationHeader.replace(/^Bearer\s+/i, "").trim() || null;
}

function extractTokenFromValue(value: unknown, depth = 0): string | null {
  if (depth > 3 || !isRecord(value)) {
    return null;
  }

  for (const key of TOKEN_KEYS) {
    const token = readString(value[key]);
    if (token) {
      return token;
    }
  }

  for (const key of NESTED_TOKEN_KEYS) {
    const token = extractTokenFromValue(value[key], depth + 1);
    if (token) {
      return token;
    }
  }

  return null;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function notifyAuthTokenChanged(): void {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new Event(AUTH_TOKEN_CHANGE_EVENT));
}
